import React, { useState, useEffect } from 'react';
import { EmptyState, getScoreNum } from './shared';

const PersonaMatch = ({ personaInsights, handleDownload }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const Section = ({ color, label, content, isMobile }) => (
    <div style={{ 
      padding: isMobile ? '10px 16px' : '12px 20px', 
      borderTop: '1px solid #e2e8f0' 
    }}>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: isMobile ? 8 : 9, 
        fontWeight: 700, 
        letterSpacing: '0.08em',
        textTransform: 'uppercase', 
        color, 
        marginBottom: isMobile ? 5 : 6,
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? 5 : 6
      }}>
        <span style={{ 
          width: isMobile ? 7 : 8, 
          height: isMobile ? 7 : 8, 
          borderRadius: 2, 
          background: color, 
          display: 'inline-block', 
          flexShrink: 0 
        }} />
        {label}
      </div>
      <p style={{ 
        fontSize: isMobile ? 11 : 11.5, 
        color: '#6e6e73', 
        lineHeight: 1.5, 
        margin: 0,
        wordBreak: 'break-word',
      }}>
        {content}
      </p>
    </div>
  );

  const extract = (text, startKeys, endKeys) => {
    const startPattern = startKeys.join('|');
    const endPattern = endKeys.join('|');
    const regex = new RegExp(`(?:${startPattern})[:\\s']*(.+?)(?=(?:${endPattern})[:\\s']*|$)`, 'is');
    const match = text.match(regex);
    return match ? match[1].trim() : '';
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: isMobile ? 16 : (isTablet ? 20 : 24),
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between' 
      }}>
        <h2 style={{ 
          fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
          fontWeight: 800, 
          letterSpacing: '-0.02em', 
          color: '#0f172a', 
          margin: 0 
        }}>
          Persona Insights & Match Score
        </h2>
      </div>

      {/* Cards */}
      {personaInsights.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'), 
          gap: isMobile ? 12 : 16, 
          alignItems: 'start',
          width: '100%',
        }}>
          {personaInsights.map((persona, i) => {
            const score = getScoreNum(persona.score);
            const matchColor = score >= 85 ? '#10F090' :      // Excellent — emerald
                   score >= 70 ? '#22D3EE' :      // Good      — cyan
                   score >= 50 ? '#F59E0B' :      // Needs Work — amber
                   '#B5325B';                     // Critical   — rose/crimson
            const desc = persona.desc || '';

            // Fixed color sequence - 5 distinct colors
            const colorSequence = ['#2563eb', '#1b8c2e', '#d12b2b', '#7c3aed', '#9A6700'];
            
            // Dynamically extract all sections with word: pattern
            const extractAllSections = (text) => {
              const sections = [];
              
              // Pattern to match any word/phrase followed by colon and content until next word: or end
              const sectionPattern = /([A-Za-z][A-Za-z\s\-]+?):\s*([^:]+?)(?=\s+[A-Za-z][A-Za-z\s\-]+?:|\s*$)/gi;
              
              let match;
              const matchedRanges = [];
              
              // Find all sections
              while ((match = sectionPattern.exec(text)) !== null) {
                const label = match[1].trim();
                const content = match[2].trim();
                
                sections.push({
                  label: label,
                  content: content,
                  index: match.index,
                  length: match[0].length
                });
                
                matchedRanges.push({ start: match.index, end: match.index + match[0].length });
              }
              
              // Handle any remaining colons that might have been missed
              const colonMatches = text.matchAll(/([A-Za-z][A-Za-z\s\-]+?):\s*([^]+?)(?=$)/gi);
              for (const colonMatch of colonMatches) {
                const isCaptured = matchedRanges.some(range => 
                  Math.abs(range.start - colonMatch.index) < 5
                );
                
                if (!isCaptured) {
                  const label = colonMatch[1].trim();
                  const content = colonMatch[2].trim();
                  
                  sections.push({
                    label: label,
                    content: content,
                    index: colonMatch.index,
                    length: colonMatch[0].length
                  });
                  
                  matchedRanges.push({ start: colonMatch.index, end: colonMatch.index + colonMatch[0].length });
                }
              }
              
              // Extract main description (text not part of any section)
              let mainDesc = text;
              matchedRanges.sort((a, b) => b.start - a.start).forEach(range => {
                mainDesc = mainDesc.slice(0, range.start) + mainDesc.slice(range.end);
              });
              
              mainDesc = mainDesc.replace(/\s+/g, ' ').trim();
              sections.sort((a, b) => a.index - b.index);
              
              return { sections, mainDesc };
            };

            const { sections, mainDesc } = extractAllSections(desc);

            return (
              <div key={i} style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: isMobile ? 12 : 16,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                minWidth: 0,
              }}>

                {/* Name + desc */}
                <div style={{ 
                  padding: isMobile ? '16px 16px 12px' : '18px 20px 14px' 
                }}>
                  <p style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    fontWeight: 700, 
                    color: '#1d1d1f', 
                    lineHeight: 1.3, 
                    margin: 0,
                    wordBreak: 'break-word',
                  }}>
                    {persona.name}
                  </p>
                  {mainDesc && mainDesc !== desc && (
                    <p style={{ 
                      fontSize: isMobile ? 11 : 12, 
                      color: '#6e6e73', 
                      marginTop: isMobile ? 5 : 6, 
                      marginBottom: 0, 
                      lineHeight: 1.5,
                      wordBreak: 'break-word',
                    }}>
                      {mainDesc}
                    </p>
                  )}
                </div>

                {/* Dynamically render all extracted sections with fixed color sequence */}
                {sections.map((section, idx) => {
                  const colorIndex = idx % colorSequence.length;
                  const sectionColor = colorSequence[colorIndex];
                  
                  return (
                    <Section 
                      key={idx}
                      color={sectionColor} 
                      label={section.label} 
                      content={section.content}
                      isMobile={isMobile}
                    />
                  );
                })}

                {/* If no sections were found but there's description text, show it as main content */}
                {sections.length === 0 && mainDesc && (
                  <div style={{ 
                    padding: isMobile ? '0 16px 12px' : '0 20px 14px' 
                  }}>
                    <p style={{ 
                      fontSize: isMobile ? 11 : 12, 
                      color: '#6e6e73', 
                      lineHeight: 1.5,
                      wordBreak: 'break-word',
                    }}>
                      {mainDesc}
                    </p>
                  </div>
                )}

                {/* Match score */}
                {score > 0 && (
                  <div style={{
                    padding: isMobile ? '12px 16px' : '14px 20px',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobile ? 8 : 10
                  }}>
                    <div>
                      <p style={{ 
                        fontSize: isMobile ? 16 : 18, 
                        fontWeight: 800, 
                        color: matchColor, 
                        margin: 0, 
                        lineHeight: 1 
                      }}>
                        {score}%
                      </p>
                      <p style={{ 
                        fontSize: isMobile ? 10 : 11, 
                        color: '#6e6e73', 
                        margin: '2px 0 0' 
                      }}>
                        Match
                      </p>
                    </div>
                    <div style={{ 
                      flex: 1, 
                      height: isMobile ? 4 : 5, 
                      background: '#f0f1f5', 
                      borderRadius: 3, 
                      overflow: 'hidden' 
                    }}>
                      <div style={{
                        height: '100%', 
                        width: `${score}%`,
                        background: matchColor, 
                        borderRadius: 3,
                        transition: 'width 0.8s ease'
                      }} />
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ 
          background: '#ffffff', 
          borderRadius: isMobile ? 12 : 16, 
          border: '1px solid #e2e8f0', 
          padding: isMobile ? '20px 16px' : '24px 28px' 
        }}>
          <EmptyState
            icon="Users"
            title="No persona data"
            desc="Investor persona match scores will populate from your HTML report."
          />
        </div>
      )}

    </div>
  );
};

export default PersonaMatch;